/**
 * Bahadura Permanent RL / Temporary RL shared constants.
 * Mirrors civic-plan-backend/api/config/phaseConstants.js exactly.
 *
 * rl_application_type decides which journey an application takes:
 *  - PERMANENT: runs the plain original flow unmodified, no phase branching at all.
 *  - TEMPORARY: runs the original flow as Phase 1, then optionally the Phase 2
 *    continuation flow (current_phase / permanent_rl_status apply only here).
 */

export const RL_APPLICATION_TYPE = {
  PERMANENT: "Permanent",
  TEMPORARY: "Temporary",
} as const;

export const PHASE = {
  TEMP_RL: "Phase_1",
  PERMANENT_RL: "Phase_2",
} as const;

export const PERMANENT_RL_STATUS = {
  DRAFT: "Draft",
  PENDING_L1: "Pending Level 1 Review",
  PENDING_L2: "Pending Level 2 Review",
  PENDING_L3: "Pending Level 3 Review",
  APPROVED: "Approved",
  RETURNED_L1: "Returned By Level 1 Reviewer",
  RETURNED_L2: "Returned By Level 2 Reviewer",
  RETURNED_L3: "Returned By Level 3 Reviewer",
  REJECTED_L2: "Rejected By Level 2 Reviewer",
  REJECTED_L3: "Rejected By Level 3 Reviewer",
} as const;

export const DOCUMENT_CONFIG_ID = {
  PERMANENT_RL_K_PRAT: 9991,
  PERMANENT_RL_REGISTRY_CERT: 9992,
  TENTATIVE_RL_COPY: 10001,
  SALE_DEED: 10002,
} as const;

/**
 * The document_Type value the two Tentative-RL-continuation documents above
 * were created under in cp_document_config_admin. They're real, admin-owned
 * rows (unlike K Prat/Registry Certificate below), but deliberately not
 * mapped to any form/step, since they only ever get uploaded through
 * PermanentRLUploadModal - so a plain `docForm.documentConfigs.find(...)`
 * lookup won't find them either, same fallback need as the sentinel pair.
 */
export const PERMANENT_RL_CONTINUATION_DOCUMENT_TYPE =
  "Permanent RL Continuation Document";

/**
 * Fixed display labels for the Permanent RL Phase 2 continuation documents
 * (uploaded via PermanentRLUploadModal), keyed by document_config_id above.
 * K Prat/Registry Certificate are sentinel IDs with no real config row;
 * Tentative RL Copy/Sale Deed are real cp_document_config_admin rows that
 * just aren't mapped to any form - either way, a plain
 * `docForm.documentConfigs.find(...)` lookup never matches them.
 */
export const PERMANENT_RL_DOCUMENT_LABELS: Record<number, string> = {
  [DOCUMENT_CONFIG_ID.PERMANENT_RL_K_PRAT]: "K Prat Document",
  [DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT]: "Registry Certificate",
  [DOCUMENT_CONFIG_ID.TENTATIVE_RL_COPY]: "Tentative RL Copy",
  [DOCUMENT_CONFIG_ID.SALE_DEED]: "Sale Deed",
};

/**
 * WHAT: Single shared lookup for a document's display label in every
 * "Documents" table across the app (L1/L2/L3 review + payment-confirm
 * screens, view-application-details for both applicant and admin).
 * WHY: User instruction (2026-09-24) — the Permanent RL Phase 2 K Prat
 * Document / Registry Certificate (document_config_id 9991/9992) were
 * uploaded successfully but every one of those 8 screens independently
 * re-implemented the same `docForm.documentConfigs.find(...)` lookup with
 * no fallback for these two sentinel IDs, so they were silently filtered
 * out everywhere ("No Documents" shown despite a successful upload).
 * Centralizing here means any future screen reuses this instead of
 * duplicating (and re-missing) the same lookup.
 * HOW: tries the real form-configured document label first (regular
 * documents, including ones re-submitted in Phase 2 under the same
 * config id), then falls back to the fixed Permanent RL label map above.
 * Returns null (caller should filter the row out) if neither matches.
 */
export function resolveDocumentLabel(
  docForm:
    | { documentConfigs?: Array<{ document_config_id: number; document_label?: string }> }
    | null
    | undefined,
  configId: number
): string | null {
  const configuredLabel = docForm?.documentConfigs?.find(
    (e) => e.document_config_id === configId
  )?.document_label;

  if (configuredLabel) return configuredLabel;

  return PERMANENT_RL_DOCUMENT_LABELS[configId] || null;
}

/**
 * The "Application Type" field (uid rl_application_type) is a DB-driven
 * `configurable-select` field (cp_form_fields / cp_field_options), same as
 * road_leading_to_land. Its raw react-hook-form value is therefore the
 * numeric cp_field_options.id of the chosen option (see RenderSelect's
 * `valueAsNumber: true`), NOT the "Permanent"/"Temporary" string — that
 * string conversion only happens server-side in transformApplication().
 * These option IDs are inserted explicitly in the DB migration so both
 * sides can agree on fixed values, same convention as the 9991/9992
 * document_config_id's above.
 */
export const RL_APPLICATION_TYPE_OPTION_ID = {
  PERMANENT: 9993,
  TEMPORARY: 9994,
} as const;

/**
 * True only for a Temporary-type application currently in its Phase 2
 * (post-completion Permanent RL) continuation. A Permanent-type application
 * never satisfies this, regardless of current_phase.
 */
export function isPhase2Continuation(application: any): boolean {
  return (
    application?.rl_application_type === RL_APPLICATION_TYPE.TEMPORARY &&
    application?.current_phase === PHASE.PERMANENT_RL
  );
}

/**
 * "Permanent"/"Tentative" display label for the navbar Application Type
 * field. The Temporary type reads as "Tentative" here even though the
 * underlying RL_APPLICATION_TYPE.TEMPORARY constant and the stored
 * rl_application_type value both stay "Temporary" - only the label users
 * actually see was renamed.
 */
export function getApplicationTypeLabel(rlApplicationType?: unknown): string {
  if (rlApplicationType === RL_APPLICATION_TYPE.PERMANENT) return "Permanent";
  if (rlApplicationType === RL_APPLICATION_TYPE.TEMPORARY) return "Tentative";
  return "Nil";
}

/**
 * "Phase I"/"Phase II" display label for the navbar Application Phase field.
 */
export function getApplicationPhaseLabel(currentPhase?: unknown): string {
  if (currentPhase === PHASE.PERMANENT_RL) return "Phase II";
  if (currentPhase === PHASE.TEMP_RL) return "Phase I";
  return "Nil";
}

/**
 * Combined "Permanent" / "Tentative (Phase I)" / "Tentative (Phase II)"
 * display label for the Application Type column on application-listing
 * tables. Same renaming as getApplicationTypeLabel above - Temporary shows
 * as "Tentative" to the user, the stored value and constant name are
 * untouched.
 */
export function getApplicationTypeColumnLabel(
  application:
    | { rl_application_type?: unknown; current_phase?: unknown }
    | null
    | undefined
): string {
  if (!application) return "NA";
  if (application.rl_application_type === RL_APPLICATION_TYPE.PERMANENT) {
    return "Permanent";
  }
  if (application.rl_application_type === RL_APPLICATION_TYPE.TEMPORARY) {
    return application.current_phase === PHASE.PERMANENT_RL
      ? "Tentative"
      : "Tentative";
  }
  return "NA";
}

/**
 * Plot-detail / land-possession / application-type fields that must always
 * render on the L1/L2/L3 review screens' <ApplicationList /> regardless of
 * whether `selectedFields` resolved (it can come up empty if service-id to
 * service-name resolution fails), matching the existing force-include
 * precedent already used there for typeOfApplicationSubmitterBy/submittedBy.
 */
export const PLOT_AND_LAYOUT_FIELD_UIDS = [
  "rl_application_type",
  "unauthorized_layout_name",
  "area_as_per_7_12",
  "area_as_per_revenue_record",
  "area_as_per_sale_deed",
  "area_under_possession",
  "road_leading_to_land",
  "plot_usage_dp",
  "address",
] as const;
